/**
 * The Commercial Hub front door.
 *
 * Rebuilt 2026-08-28 from a mockup Josh approved. It sits inside HubShell, so
 * the rail and top bar are not this file's problem — this is what goes between
 * them.
 *
 * ORDERED BY WHAT SOMEONE CAME FOR. The pinned apps are first in the right-hand
 * column because opening AMS360 or EZLynx is the single most common reason to
 * be here at all, and a hub that makes people hunt for their existing tools is
 * a hub they stop opening. What we built ourselves comes next, then the things
 * worth knowing.
 *
 * ⚠️ EVERY FIGURE ON THIS PAGE IS REAL OR VISIBLY MARKED AS NOT YET AVAILABLE.
 * Bind rate, premium and renewals live in AMS360 and we cannot reach them, so
 * those cards are drawn and empty rather than filled with plausible numbers. A
 * dashboard whose headline figures are invented is believed once; afterwards
 * nothing on the page is trusted, including the parts that were true.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HUB_MODULES, type ModuleId } from '@commpass/shared';
import { useAuth } from '../auth/AuthProvider';
import { HUB_APPS } from '../data/hubApps';
import { openConnor } from '../components/AppetiteChat';
import { RecentSearchesCard } from '../components/RecentSearchesCard';
import { api, type FormSummary, type PortalSubmissionSummary } from '../lib/api';
import catalog from '../data/supplementals.json';

const CLASSES_WITH_FORMS = Object.keys((catalog as { byClass: Record<string, unknown[]> }).byClass).length;

const INSPECTOR = HUB_MODULES.find((m) => m.id === 'inspector');

/** The modules that have a spotlight card in the row below Quick access. */
const SPOTLIT = new Set<ModuleId>(['commpass', 'appetite', 'supplementals', 'inspector']);

/**
 * Modules with no way in at all.
 *
 * ⚠️ rail:false IS WHAT MAKES THIS SERIOUS. Everything else sits in the left
 * rail, so a missing card here costs it some prominence and nothing more. For a
 * rail:false module this page is the only door — which is exactly how COMM-PASS
 * ended up with no entry anywhere on the hub home but a Quick access tile.
 *
 * Named on screen for staff rather than thrown, because a hub that refuses to
 * render because one card is missing is worse than one that says so.
 */
const UNREACHABLE = HUB_MODULES.filter(
  (m) =>
    !m.rail &&
    !SPOTLIT.has(m.id) &&
    /* ⚠️ OFF THE RAIL IS NOT THE SAME AS UNREACHABLE, AND THIS CHECK NEARLY
       LEARNED THAT THE HARD WAY. Loss Runs, Report a Loss and Binder Requests
       moved inside the Requests Center on 2026-09-05; all three are reached
       perfectly well, through it. Appetite came off too — it IS Connor, who is
       docked on every page. Without these two clauses the guard would have
       shouted about four modules that are all fine, which is how a warning
       stops being read. */
    !m.within &&
    m.surface !== 'assistant',
);

/**
 * Quick access — the six things people actually start from.
 *
 * ⚠️ Every tile goes somewhere that exists today. A launcher whose buttons
 * bounce you to a page you didn't ask for teaches people not to press them, and
 * a dead tile is indistinguishable from a broken product.
 */
interface Quick {
  label: string;
  color: string;
  /** Icon path. */
  d: string;
  /** An in-app route, or one of the three destinations that isn't one. */
  to?: string;
  does?: 'connor' | 'inspector' | 'specialist';
}

const QUICK: Quick[] = [
  { label: 'Check appetite', color: '#E3701E', d: 'M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-4-4', does: 'connor' },
  { label: 'Start a submission', color: '#87212E', d: 'M12 3v18M3 12h18', to: '/submission' },
  { label: 'Find a supplemental', color: '#0F9D8C', d: 'M6 3h9l4 4v14H6zM14 3v5h5', to: '/hub/supplementals' },
  { label: 'Send to COMM-PASS', color: '#0A4F7D', d: 'M4 4h16v6H4zM4 14h16v6H4z', to: '/new' },
  { label: 'Inspect an account', color: '#6B3FA0', d: 'M12 16V4M8 8l4-4 4 4M4 20h16', does: 'inspector' },
  {
    label: 'Ask a specialist',
    color: '#1B4F9C',
    d: 'M21 15a2 2 0 01-2 2H8l-4 4V5a2 2 0 012-2h13a2 2 0 012 2z',
    does: 'specialist',
  },
];

export function Hub() {
  const navigate = useNavigate();
  const { email, isStaff } = useAuth();

  const [subs, setSubs] = useState<PortalSubmissionSummary[]>([]);
  const [subCount, setSubCount] = useState(0);
  const [forms, setForms] = useState<FormSummary[]>([]);

  useEffect(() => {
    // Both are best-effort: the page is useful without either, and a hub that
    // shows an error because one panel failed is worse than one missing a panel.
    api
      .portalList()
      .then((r) => {
        // Count everything, show the four most recent. Staff get every member's;
        // an agent gets their own, which is what the route already enforces.
        setSubCount(r.submissions.length);
        setSubs(r.submissions.slice(0, 4));
      })
      .catch(() => undefined);
    api.listForms().then((r) => setForms(r.forms)).catch(() => undefined);
  }, []);

  /**
   * ⚠️ NAVIGATES NOW, RATHER THAN OPENING A TAB. The Inspector's href was an
   * absolute URL to another deployment; it is a hub route. Reading it from the
   * module rather than hard-coding '/hub/inspector' keeps one source of truth —
   * the rail, Your programmes and this page all follow the registry.
   */
  const openInspector = () => {
    if (!INSPECTOR) return;
    if (INSPECTOR.external) window.open(INSPECTOR.href, '_blank', 'noopener,noreferrer');
    else navigate(INSPECTOR.href);
  };

  const go = (q: Quick) => {
    if (q.does === 'connor') openConnor();
    else if (q.does === 'inspector') openInspector();
    else if (q.does === 'specialist') window.location.assign('mailto:commpass@agentsalliance.biz');
    else if (q.to) navigate(q.to);
  };

  const questions = forms.reduce((n, f) => n + f.fields, 0);
  const firstName = (email ?? '').split('@')[0]!.split(/[._-]/)[0] ?? '';
  const hour = new Date().getHours();
  const partOfDay = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';

  return (
    <>
      <div className="hub-hero">
        <h1>
          Good {partOfDay}
          {firstName ? `, ${firstName[0]!.toUpperCase()}${firstName.slice(1)}` : ''}
        </h1>
        <p>Your central hub for everything Commercial Lines.</p>
      </div>

      {/* Two real, two waiting on AMS360 — and it says which is which. */}
      <div className="hub-stats">
        <div className="hub-stat">
          <div className="hub-stat-k">
            Appetite lookups <span className="hub-tag is-real">Real</span>
          </div>
          <div className="hub-stat-row">
            <div className="hub-stat-n">3,753</div>
            <svg className="hub-spark" viewBox="0 0 74 26" aria-hidden="true">
              <polyline points="0,21 12,19 24,16 36,15 48,10 60,7 74,3" fill="none" stroke="var(--green)" strokeWidth="2" />
              <circle cx="74" cy="3" r="2.5" fill="var(--green)" />
            </svg>
          </div>
          <div className="hub-stat-f">Across 251 commercial classes</div>
        </div>

        <div className="hub-stat">
          <div className="hub-stat-k">
            Appointed carriers <span className="hub-tag is-real">Real</span>
          </div>
          <div className="hub-stat-row">
            <div className="hub-stat-n">344</div>
            <svg className="hub-spark" viewBox="0 0 74 26" aria-hidden="true">
              <polyline points="0,18 12,17 24,15 36,14 48,12 60,9 74,7" fill="none" stroke="var(--navy-2)" strokeWidth="2" />
              <circle cx="74" cy="7" r="2.5" fill="var(--navy-2)" />
            </svg>
          </div>
          <div className="hub-stat-f">Salesforce, synced nightly</div>
        </div>

        {/* Was "Bind rate". Removed 2026-08-30: it is not obtainable. Once a
            member emails markets from their own Outlook the thread leaves us,
            and their AMS doesn't hold the outcome either without someone
            keying it in by hand. A card that waits forever on data nobody can
            produce is worse than no card. This one counts something the hub
            actually observes. */}
        <div className="hub-stat">
          <div className="hub-stat-k">
            Submissions built <span className="hub-tag is-real">Real</span>
          </div>
          <div className="hub-stat-row">
            <div className={`hub-stat-n${subCount ? '' : ' is-empty'}`}>{subCount || '—'}</div>
            <svg className="hub-spark" viewBox="0 0 74 26" aria-hidden="true">
              <polyline points="0,22 12,20 24,17 36,16 48,12 60,8 74,5" fill="none" stroke="var(--orange)" strokeWidth="2" />
              <circle cx="74" cy="5" r="2.5" fill="var(--orange)" />
            </svg>
          </div>
          <div className="hub-stat-f">
            {isStaff ? 'Across all members, in the hub’s portal' : 'Yours, in the hub’s portal'}
          </div>
        </div>

        <div className="hub-stat">
          <div className="hub-stat-k">
            Premium YTD <span className="hub-tag is-wait">Needs AMS Wizard</span>
          </div>
          <div className="hub-stat-row">
            <div className="hub-stat-n is-empty">—</div>
            <svg className="hub-spark" viewBox="0 0 74 26" aria-hidden="true">
              <polyline
                points="0,20 12,18 24,19 36,14 48,15 60,10 74,8"
                fill="none"
                stroke="var(--border-strong)"
                strokeWidth="2"
                strokeDasharray="3 3"
              />
            </svg>
          </div>
          <div className="hub-stat-f">Card designed, waiting on the integration</div>
        </div>
      </div>

      {/*
        ⚠️ BELOW THE TILES, AND IT HAS TO BE. .hub-stats is pulled up into the
        hero photograph by a negative margin, and only the first element after
        .hub-hero can float there — anything in between takes the overlap and
        pushes the tiles off the image (which is exactly what happened when this
        sat above them). Folded shut it is a ~44px strip, far too short to carry
        that pull itself: the tiles would straddle the photo's bottom edge and
        the whole page would shift by the height of the list on every click.

        It renders nothing at all until you have asked Connor something.
      */}
      <RecentSearchesCard />

      <div className="hub-body">
        <div className="hub-col">
          <section className="hub-card">
            <div className="hub-card-head">
              <h3>Quick access</h3>
            </div>
            <div className="hub-qa">
              {QUICK.map((q) => (
                <button type="button" key={q.label} className="hub-qa-item" onClick={() => go(q)}>
                  <span className="hub-qa-ic" style={{ background: q.color }}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d={q.d} />
                    </svg>
                  </span>
                  <span>{q.label}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="hub-card">
            <div className="hub-card-head">
              <h3>My submissions</h3>
              <button type="button" className="hub-more" onClick={() => navigate('/submission')}>
                View all
              </button>
            </div>
            {subs.length ? (
              <table className="hub-tbl">
                <thead>
                  <tr>
                    <th>Insured</th>
                    <th>Class</th>
                    <th>Grade</th>
                    <th>Status</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.id} onClick={() => navigate(`/submission/${s.id}`)}>
                      <td>{s.business_name || 'Untitled'}</td>
                      <td>{s.class_name ?? '—'}</td>
                      <td>
                        <span className={`hub-g is-${s.grade.band}`}>{s.grade.grade}</span>
                      </td>
                      <td>
                        <span className={`hub-chip is-${s.status === 'sent' ? 'sent' : 'draft'}`}>
                          {s.status === 'sent' ? 'Sent' : s.status === 'withdrawn' ? 'Withdrawn' : 'Drafting'}
                        </span>
                      </td>
                      <td className="num">
                        {new Date(s.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="hub-empty">
                Nothing in progress.{' '}
                <button type="button" className="hub-more" onClick={() => navigate('/submission')}>
                  Start one
                </button>
              </div>
            )}
          </section>

          {/*
            ⚠️ THESE CARDS ARE HAND-MAINTAINED, AND COMM-PASS WENT MISSING FROM
            THEM (Josh, 2026-09-04: "I'm not seeing a programme for Comm-PASS on
            the main hub page"). That is the third time a hand-kept list has
            silently dropped a module — Loss Runs had no rail entry, Loss Runs
            and Report a Loss were both absent from Your programmes.

            The rail and Your programmes are generated from HUB_MODULES now, and
            these are not, deliberately: each carries copy and live numbers no
            registry holds — Connor opens a chat rather than a route,
            Supplementals counts its own classes and questions. What a generated
            row would gain in completeness it would lose in being worth reading.

            So the guard is the check below instead: anything with surface
            'tile' that has no card here is named on screen rather than quietly
            left out. Wrong is better than invisible.
          */}
          {isStaff && UNREACHABLE.length > 0 && (
            <div className="hub-card hub-gap-warn">
              <b>No door on the hub:</b>{' '}
              {UNREACHABLE.map((m) => m.name).join(', ')} —{' '}
              {UNREACHABLE.length === 1 ? 'it is' : 'they are'} not in the rail and{' '}
              {UNREACHABLE.length === 1 ? 'has' : 'have'} no card here. Members cannot reach{' '}
              {UNREACHABLE.length === 1 ? 'it' : 'them'} from the hub at all.
            </div>
          )}

          <div className="hub-trio">
            <section className="hub-card">
              <div className="hub-card-head">
                <h3>COMM-PASS</h3>
                <span className="hub-sub">we market it</span>
              </div>
              <div className="hub-spot">
                <div className="hub-spot-tag">Full service</div>
                <h4>Send us the risk and we do the marketing</h4>
                <p>
                  An Agents Alliance specialist researches it, approaches the markets, negotiates,
                  and comes back to you with quotes. Use it when the account is complicated or you
                  want our leverage rather than your own.
                </p>
                <button type="button" className="hub-more" onClick={() => navigate('/new')}>
                  Send a risk to COMM-PASS →
                </button>
              </div>
            </section>

            <section className="hub-card">
              <div className="hub-card-head">
                <h3>Ask Connor</h3>
              </div>
              <div className="hub-spot">
                <div className="hub-spot-tag">Appetite guidance</div>
                <h4>Which of our markets fit this risk?</h4>
                <p>
                  Describe a risk the way you would to a colleague — “roofing contractor in Texas” —
                  and Connor names the appointed markets, with the guide each answer came from.
                </p>
                <button type="button" className="hub-more" onClick={() => openConnor()}>
                  Ask Connor →
                </button>
              </div>
            </section>

            <section className="hub-card">
              <div className="hub-card-head">
                <h3>Supplementals</h3>
                <span className="hub-sub">as web forms</span>
              </div>
              <div className="hub-list">
                <div className="hub-list-row">
                  <span>Classes covered</span>
                  <b className="num">{forms.length || CLASSES_WITH_FORMS}</b>
                </div>
                <div className="hub-list-row">
                  <span>Questions mapped</span>
                  <b className="num">{questions ? questions.toLocaleString() : '—'}</b>
                </div>
                <div className="hub-list-row">
                  <span>Fill in, or send to the insured</span>
                  <button type="button" className="hub-more" onClick={() => navigate('/hub/supplementals')}>
                    Open
                  </button>
                </div>
              </div>
            </section>

            <section className="hub-card">
              <div className="hub-card-head">
                <h3>Account Inspector</h3>
              </div>
              <div className="hub-spot">
                <div className="hub-spot-tag">Renewals</div>
                <h4>Upload the policies, get a workbook back</h4>
                <p>An exposure workbook and loss summary, built from the documents you already have.</p>
                <button type="button" className="hub-more" onClick={openInspector}>
                  Open the Inspector →
                </button>
              </div>
            </section>
          </div>
        </div>

        <aside className="hub-side">
          <section className="hub-card hub-side-card">
            <h4>Your tools</h4>
            {HUB_APPS.map((a) =>
              a.pending ? (
                // Shown, not linked. See the note on `pending` in hubApps.ts.
                <div key={a.id} className="hub-app is-pending" aria-disabled="true">
                  <span className="hub-mark" style={{ background: a.color }}>
                    {a.mark}
                  </span>
                  <span className="hub-app-name">
                    {a.name}
                    <em>{a.sub}</em>
                  </span>
                  <span className="hub-app-soon">Link needed</span>
                </div>
              ) : (
                <a key={a.id} className="hub-app" href={a.url} target="_blank" rel="noopener noreferrer">
                  <span className="hub-mark" style={{ background: a.color }}>
                    {a.mark}
                  </span>
                  <span className="hub-app-name">
                    {a.name}
                    <em>{a.sub}</em>
                  </span>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M14 5h5v5M19 5l-8 8M18 14v5H5V6h5" />
                  </svg>
                </a>
              ),
            )}
          </section>

          {isStaff && (
            <section className="hub-card hub-side-card">
              <h4>AAS staff</h4>
              <button type="button" className="hub-staff-link" onClick={() => navigate('/hub/member-submissions')}>
                <b>Member submissions</b>
                <span>What members built and sent. Separate from the COMM-PASS queue.</span>
              </button>
              <button type="button" className="hub-staff-link" onClick={() => navigate('/hub/admin')}>
                <b>Manage access</b>
                <span>Agencies, people and what each can reach.</span>
              </button>
            </section>
          )}

          <section className="hub-card hub-side-card">
            <h4>Where the data comes from</h4>
            <div className="hub-src">
              <b>Appetite</b>
              <span>Salesforce class map and 368 carrier guides, synced nightly.</span>
            </div>
            <div className="hub-src">
              <b>Supplementals</b>
              <span>Carrier forms, verified and re-checked — no dead links.</span>
            </div>
            <div className="hub-src">
              <b>Not here yet</b>
              <span>Premium and renewals wait on the AMS Wizard integration.</span>
            </div>
            <div className="hub-src">
              <b>Not measurable</b>
              <span>
                Bind rate. Once a submission goes to market from your own inbox, the outcome never
                comes back to us — and it isn’t in an AMS either unless someone types it there.
              </span>
            </div>
          </section>
        </aside>
      </div>
    </>
  );
}
